import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {getCall, postCall, fileUpload} from '../helpers/request';
import GlobalContext from '../helpers/context';
import {IMPORT_ROLES} from '../settings';
import HeaderButton from '../component/atoms/HeaderButton';
import LinkButton from '../component/atoms/LinkButton';
import ActionButton from '../component/atoms/ActionButton';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import CustomAccordion from '../component/molecule/CustomAccordion';
import ResearcherId from '../component/molecule/ResearcherId';
import DocumentStats from '../component/molecule/DocumentStats';
import NameVariants from '../component/molecule/NameVariants';
import {checkUserRoles, getUser} from '../helpers/authentication';
import {autoPluralize} from '../helpers/pluralizer';
import Organization from '../component/molecule/Organization';
import CurationProfileStatusLabel from '../curation_profile/CurationProfileStatusLabel';
import CurationProfileVerifiedLabel from '../curation_profile/CurationProfileVerifiedLabel';
import statusOptions from '../curation_profile/CurationProfileStatusOptions';
import verifiedOptions from '../curation_profile/CurationProfileVerifiedOptions';
import CurationProfileData from '../curation_profile/CurationProfileData';
import Autocomplete from '../component/Autocomplete';
import history from '../helpers/history';
import FileUpload from '../component/FileUpload';
import CustomConfirmationDialog from './CustomConfirmDialog';
import {getUrlParams} from '../helpers/location';
import Box from "@mui/material/Box";


const default_status_filter = 0;
const default_verified_filter = 0;


const StyledDetailHeader = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30
}));

const StyledSearchArea = styled(Grid)({
    marginBottom: 20,
    marginTop: -4
});

const StyledDetailHeaderMeta = styled(Typography)(({ theme }) =>({
    marginTop: 10,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height
}));

const StyledCurationProfileList = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30,
    minWidth: theme.custom.min_width
}));

const CurationProfileHeader = styled(Grid)(({ theme }) =>({
    padding: '10px 41px 10px 16px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: theme.custom.font.bold
}));

const StyledCurationProfileListItem = styled(Grid)(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const CurationProfileOptions = styled(Grid)({
    textAlign: 'right',
    paddingRight: 15
});

const StyledStatusFilter = styled(Select)(({ theme }) =>({
    marginLeft: 10,
    position: 'relative',
    top: -4,
    color: theme.palette.secondary.contrastText,
    fontFamily: theme.typography.fontFamily,
}));

const StyledEInfoIcon = styled(InfoOutlinedIcon)({
    cursor: 'pointer',
    verticalAlign: 'middle',
    marginLeft: 5
});

const StyledInfoPopoverContent = styled('div')({
    padding: '0 15px 0 15px',
    maxWidth: 400
});


const styles = (theme) => ({
    statusFilterColor: {
        color: theme.palette.secondary.contrastText,
    }
});


class WorkspaceDetails extends React.Component {

    constructor(props) {
        super(props);

        this.params = getUrlParams();

        this.state = {
            name: '',
            description: '',
            customer_id: '',
            created: '',
            owner_name: '',
            curation_profiles: undefined,
            curation_profile_count: '',
            statusFilter: default_status_filter,
            verifiedFilter: default_verified_filter,
            curation_profiles_details: {},
            next_page: undefined,
            openFileUploadDialog: false,
            anchorEl: null,
            import_req_fields: '',
            import_opt_fields: '',
            custom_confirmation_dialog: {},
            user: null
        };

        this.is_mounted = false;
        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.get_workspace_url = concatUrl(window.ROOT_URL, 'api', 'workspace', 'details');
        this.delete_curation_profile_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'delete');
        this.import_curation_profiles_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'import');
        this.search_name_url = concatUrl(window.ROOT_URL, 'api', 'workspace', 'search_name');
    }

    componentDidMount(){
        this.is_mounted = true;
        this.loadWorkspaceDetails();
        this.props.updatedCallback();
        getUser((user) => {
            if(this.is_mounted) {
                this.setState({user: user});
            }
        });
    }

    componentDidUpdate(){
        this.props.updatedCallback();
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    componentDidUpdate(_, prevState){
        this.context.checkUserAccess(this.conditionCallback);
        if (prevState.next_page !== this.state.next_page && this.state.next_page){
            this.register_lazy_loading();
        }
        if (prevState.verifiedFilter !== this.state.verifiedFilter){
            this.loadWorkspaceDetails();
        }
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    stopPropagation = (event) => {
        event.stopPropagation();
    };

    onDialogCancel = (event) => {
        this.stopPropagation(event);
        this.setState({
            custom_confirmation_dialog: {}
        });
    };

    register_lazy_loading = () => {
        let options = {
          root: null,
          threshold: 0.5
        };
        if (this.state.next_page){
            let observer = new IntersectionObserver(this.loadNextPage, options);
            let target = document.querySelector('#nextPage');
            observer.observe(target);
        }
    };

    loadNextPage = (entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && this.state.next_page && !this.state.loading_next_page) {
                this.loadNextPageDetails();
            }
        });
    };

    handleInfoIconClick = (event) => {
        this.setState({
            anchorEl: event.currentTarget,
        });
    };

    handleInfoIconClose = () => {
        this.setState({
            anchorEl: null,
        });
    };

    // +++ load workspace data +++++++++++++++++++++++++++++++++++++++++++++++
    // curation_profiles contains only the first set of CP. Next CP will be loaded by
    // loadNextPageDetails triggered by lazy loading.

    loadWorkspaceDetailsFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load workspace details.');
    };

    loadWorkspaceDetailsDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            this.setState({
                name: data.name,
                description: data.description,
                customer_id: data.customer_id,
                created: data.created,
                owner_name: data.owner_name,
                curation_profiles: data.curation_profiles,
                curation_profiles_count: data.curation_profiles_count,
                next_page: data.next_page,
                import_req_fields: data.import_req_fields,
                import_opt_fields: data.import_opt_fields
            });
        }
    };

    loadWorkspaceDetails = () => {
        getCall(`${this.get_workspace_url}?id=${this.params.id}&verified_filter=${this.state.verifiedFilter}`, this.loadWorkspaceDetailsDone, this.loadWorkspaceDetailsFailed);
    };


    // +++ load next page (next block of curation profiles) +++++++++++++++++++++++++++++++++++++++

    loadNextPageFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load next page.');
        this.setState({loading_next_page: false});
    };

    loadNextPageDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            let curation_profiles = this.state.curation_profiles;
            this.setState({
                curation_profiles: curation_profiles.concat(data.curation_profiles),
                next_page: data.next_page,
                loading_next_page: false
            });
        }
    };

    loadNextPageDetails = () => {
        this.setState({loading_next_page: true});
        getCall(this.state.next_page, this.loadNextPageDone, this.loadNextPageFailed);
    };


    // +++ delete a curation profile +++++++++++++++++++++++++++++++++++++++++++++++

    deleteCurationProfileFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not delete the curation profile.');
    };

    deleteCurationProfileDone = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.loadWorkspaceDetails();
    };

    deleteCurationProfile = (curation_profile_id) => {
        postCall(this.delete_curation_profile_url, {'id': curation_profile_id, 'workspace_id': this.params.id}, this.deleteCurationProfileDone, this.deleteCurationProfileFailed);
    };


    showConfirmationDialog = (event) => {
        this.stopPropagation(event);
        let curation_profile_id = event.currentTarget.dataset.curation_profile_id;

        this.context.showConfirmationDialog({
            confirm_dialog_on_confirm: () => {this.deleteCurationProfile(curation_profile_id)},
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: `Are you sure you want to delete the curation profile "${event.currentTarget.dataset.name}"?`,
            confirm_dialog_button_text: 'delete'
        });
    };


    // +++ search for a CP name +++++++++++++++++++++++++++++++++++++++++++++++

    onNameSelect = (suggestion) => {
        history.push(`/workspaces/curation_profiles/details?id=${suggestion.id}&workspace_id=${this.params.id}`);
        document.location.reload();
    };

    autoCompleteSearchFailedCallback = () => {
        this.context.displayGlobalError('Backend service failure: Could not run auto complete.');
    };

    // +++ file upload +++++++++++++++++++++++++++++++++++++++++++++++

    openFileUploadDialog = () => {
        this.setState({'openFileUploadDialog': true});
    };

    uploadFileSaveCallback = (file) => {
        this.uploadFile(file);
        this.setState({'openFileUploadDialog': false});
    };

    uploadFileCloseCallback = () => {
        this.setState({'openFileUploadDialog': false});
    };

    uploadFileFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not upload the file.');
    };

    uploadFileDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (data.error){
             this.context.displayGlobalError(data.error);
        }else{
            let plausibility_warnings = undefined;
            if(data.plausibility_warnings){
                plausibility_warnings = data.plausibility_warnings;
            }
            if(data.statistics){
                this.showImportConfirmDialog(data.statistics, plausibility_warnings);
            }
        }
    };

    uploadFile = (file) => {
        fileUpload(this.import_curation_profiles_url + '?workspace_id=' + this.params.id, file, this.uploadFileDone, this.uploadFileFailed);
    };

    showImportConfirmDialog = (statistics, plausibility_warnings) => {
        this.setState({
            custom_confirmation_dialog: {
                is_open: true,
                on_close: this.onDialogCancel,
                on_confirm: this.approveUpload,
                plausibility_warnings: plausibility_warnings,
                statistics: statistics
            }
        });
    };

    approveUploadFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not upload the file.');
    };

    approveUploadDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.loadWorkspaceDetails();
        this.context.displayGlobalInfo(data.import_info);
    };

    approveUpload = () => {
        this.setState({
            curation_profiles: undefined,
            custom_confirmation_dialog: {}
        });
        postCall(this.import_curation_profiles_url + '?import_approved=true&workspace_id=' + this.params.id, {}, this.approveUploadDone, this.approveUploadFailed);
    };

    workspaceHeader(){
        if (this.params.id){
            return(
                <StyledDetailHeader>
                    <Grid container justifyContent="space-between">
                        <Grid>
                            <Typography variant="h5">
                                {this.state.name}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={5}>
                        <Grid size={4}>
                            <StyledDetailHeaderMeta>
                                Created: {this.state.created}
                            </StyledDetailHeaderMeta>
                        </Grid>
                        <Grid size={4}>
                            <StyledDetailHeaderMeta>
                                Owner: {this.state.owner_name}
                            </StyledDetailHeaderMeta>
                        </Grid>
                        <Grid size={4}>
                            <StyledDetailHeaderMeta>
                                Customer ID: {this.state.customer_id}
                            </StyledDetailHeaderMeta>
                        </Grid>
                    </Grid>
                </StyledDetailHeader>
            );
        }
    }

    handleExpansionPanelChange = curation_profile_id => () => {
        console.log(curation_profile_id);
        if(!this.state.curation_profiles_details[curation_profile_id]){
            // load data
            let curation_profiles_details = this.state.curation_profiles_details;
            curation_profiles_details[curation_profile_id] = <CurationProfileData
                curation_profile_id={parseInt(curation_profile_id)} workspace_id={parseInt(this.params.id)}/>;
            this.setState({
                'curation_profiles_details': curation_profiles_details
            });
        }
    };

    topOneOrganization = (current_organization) => {
        if(current_organization){
            return <Organization organization={current_organization} profileListStyle={true}/>;
        }
    };

    applyStatusFilter = (curation_profile) => {
        const show_curation_profile = parseInt(this.state.statusFilter) === default_status_filter || (curation_profile.status && curation_profile.status === this.state.statusFilter);
        if(show_curation_profile){
            return (
                <CustomAccordion
                    key={curation_profile.id}
                    dataBt={"curation_profile_item"}
                    onOpenCallback={this.handleExpansionPanelChange(curation_profile.id)}
                    summary={
                        <Box sx={{ flexGrow: 1 }}>
                            <StyledCurationProfileListItem container spacing={2}>
                                <Grid size={3}>
                                    <LinkButton text={curation_profile.full_name} linkStyle="link"
                                                linkData={{pathname: '/workspaces/curation_profiles/details', search: `?id=${curation_profile.id}&workspace_id=${this.params.id}`}}
                                                dataBt="curation_profile_details_button"/>
                                    <NameVariants variants={curation_profile.name_variants} />
                                </Grid>
                                <Grid size={3}>
                                    {this.topOneOrganization(curation_profile.current_organization)}
                                </Grid>
                                <Grid size={2}>
                                    <DocumentStats profile={curation_profile.doc_stats} showVerified={true}/>
                                </Grid>
                                <Grid size={2}>
                                    <ResearcherId id={curation_profile.researcher_id} />
                                </Grid>
                                <Grid size={1}>
                                    <CurationProfileStatusLabel status={curation_profile.status} />
                                </Grid>
                                <CurationProfileOptions size={1}>
                                    <LinkButton text="Edit" linkStyle="light" dataBt="edit_curation_profile_button"
                                                linkData={{pathname: '/workspaces/curation_profiles/edit', search: `?id=${curation_profile.id}&workspace_id=${this.params.id}`}} />
                                    <ActionButton text="Delete" linkStyle={true} dataBt="delete_curation_profile_button"
                                                  actionCallback={this.showConfirmationDialog}
                                                  actionData={{curation_profile_id: curation_profile.id, name: curation_profile.full_name}} />
                                </CurationProfileOptions>
                            </StyledCurationProfileListItem>
                        </Box>
                    }
                    content={
                        <Grid container>
                            <Grid size={12}>
                                {this.state.curation_profiles_details[curation_profile.id] ? this.state.curation_profiles_details[curation_profile.id] : ''}
                            </Grid>
                        </Grid>
                    }
                />
            );
        }
    };

    curationProfileList(){
        if(this.state.curation_profiles){
            return (
                <div data-bt="curation_profiles">
                    {this.state.curation_profiles.map((curation_profile) => this.applyStatusFilter(curation_profile))}
                    {this.state.next_page && <div id="nextPage"><ProgressSpinner /></div>}
                </div>
            );
        }
    }

    verifiedFilter = () => {
        return (
            <FormControl variant="standard">
                <StyledStatusFilter
                    id="verifiedFilter"
                    value={this.state.verifiedFilter}
                    autoWidth
                    onChange={(event) => {
                        this.setState({verifiedFilter: event.target.value});
                    }}
                    classes={{
                        root: styles.statusFilterColor,
                        icon: styles.statusFilterColor,
                    }}
                >
                    <MenuItem value={default_verified_filter} key="default">
                        All
                    </MenuItem>
                    {verifiedOptions.map((verified_option) =>
                        <MenuItem value={verified_option.key} key={verified_option.key}>
                            <CurationProfileVerifiedLabel status={verified_option.key} />
                        </MenuItem>)}
                </StyledStatusFilter>
            </FormControl>
        );
    };

    statusFilter = () => {
        return (
            <FormControl variant="standard">
                <StyledStatusFilter
                    id="statusFilter"
                    value={this.state.statusFilter}
                    autoWidth
                    onChange={(event) => {
                        this.setState({statusFilter: event.target.value});
                    }}
                    classes={{
                        root: styles.statusFilterColor,
                        icon: styles.statusFilterColor,
                    }}
                >
                    <MenuItem value={default_status_filter} key="default">
                        All
                    </MenuItem>
                    {statusOptions.map((status_option) =>
                        <MenuItem value={status_option.key} key={status_option.key}>
                            <CurationProfileStatusLabel status={status_option.key} />
                        </MenuItem>)}
                </StyledStatusFilter>
            </FormControl>
        );
    };

    fileUploadModule = () => {
        if (checkUserRoles(this.state.user, IMPORT_ROLES)){
            return (
                <span>
                    <HeaderButton text="Import curation profiles" dataBt="import_curation_profiles"
                                  actionCallback={this.openFileUploadDialog} />
                    <StyledEInfoIcon onClick={this.handleInfoIconClick}/>
                    <Popover
                        id="info_popover"
                        open={this.state.anchorEl !== null}
                        anchorEl={this.state.anchorEl}
                        onClose={this.handleInfoIconClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <StyledInfoPopoverContent>
                            {this.state.customer_id &&
                            <div>
                                <p>To import curation profiles into a workspace you have to use a CSV file providing the following fields: <strong>{this.state.import_req_fields}</strong></p>
                                <p>Beside the required fields the optional field <strong>departments</strong> is allowed as well.</p>
                                <p>Use <strong>|</strong> as a separator for multiple departments.</p>
                            </div>}
                            {!this.state.customer_id &&
                            <div>
                                <p>To import curation profiles into a workspace you have to use a CSV file providing the following fields: <strong>{this.state.import_req_fields}</strong></p>
                            </div>}
                        </StyledInfoPopoverContent>
                    </Popover>
                </span>
            );
        }
    };

    curationProfiles() {
        return (
            <StyledCurationProfileList data-bt="workspace_details">
                <Grid container direction="row" sx={{ justifyContent: "space-between"}}>
                    <Grid container direction="row" sx={{ justifyContent: "flex-start"}}>
                        <Grid size="auto">
                            <Typography variant="h5">
                                {autoPluralize(this.state.curation_profiles_count, 'Curation profile')}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container direction="row" sx={{ justifyContent: "flex-end"}}>
                        <StyledSearchArea size="3">
                            <Autocomplete id="search_name"
                                          label="Search for a name"
                                          getSuggestionsUrl={this.search_name_url + '?workspace_id=' + this.params.id}
                                          small_size={true}
                                          onSuggestionSelected={this.onNameSelect}
                                          selectionRequired={true}
                                          selectionRequiredCallback={()=>{}}
                                          autoCompleteSearchFailedCallback={this.autoCompleteSearchFailedCallback}
                            />
                        </StyledSearchArea>
                        <Grid size="3">
                            {this.fileUploadModule()}
                            <HeaderButton dataBt="create_curation_profile" text="Create curation profile"
                                          linkData={{pathname: '/workspaces/curation_profiles/create',
                                search: `?workspace_id=${this.params.id}`}} hotKey="alt+n"/>
                        </Grid>
                    </Grid>
                </Grid>
                <CurationProfileHeader container spacing={2}>
                    <Grid size={3}>
                        Name
                    </Grid>
                    <Grid size={3}>
                        Organization <br/>
                        City, State, Country
                    </Grid>
                    <Grid size={2}>
                        Publications {this.verifiedFilter()}
                        <br/>Grants
                    </Grid>
                    <Grid size={2}>
                        Researcher ID
                    </Grid>
                    <Grid size={1}>
                        Status {this.statusFilter()}
                    </Grid>
                    <Grid size={1}/>
                </CurationProfileHeader>
                {!this.state.curation_profiles && <ProgressSpinner />}
                {this.curationProfileList()}
            </StyledCurationProfileList>
        );
    }

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <div>
                    {this.workspaceHeader()}
                    {this.curationProfiles()}
                    <FileUpload open={this.state.openFileUploadDialog}
                                acceptedFiles={['text/csv']} saveCallback={this.uploadFileSaveCallback}
                                closeCallback={this.uploadFileCloseCallback}
                                title="Upload a CSV file with curation profiles"
                    />
                    <CustomConfirmationDialog data={this.state.custom_confirmation_dialog} />
                </div>
            )}
            </GlobalContext.Consumer>
        );
    }
}


WorkspaceDetails.contextType = GlobalContext;

WorkspaceDetails.propTypes = {
    updatedCallback: PropTypes.func
};

export default WorkspaceDetails;
